/**
 * cameraDrawer — pure helpers for the MCPlayback camera-picker drawer (Phase 1d).
 *
 * No RPC: everything is computed client-side from the camera state the app already holds
 * (state.server.cameras / allCameras / authServers — the same source NvrCameraTree reads).
 *
 * normalizeSlotCamera — map a RAW camera (cameras/allCameras carry `bID`) into the MCPlayback SLOT
 *   shape the data layer expects: `bCamera`/`bSerial`/`sName` (read by loadMultiCamEvents, Cell, and
 *   buildStreamUrl). dview cams carry no `bSerial`, so fall back to the current NVR serial. Carries
 *   the eview cross-server fields (sServerName/serverUrl) through when present, and the CLOUD device id
 *   (sDeviceID — cloud cams have bSerial 0 and are keyed by device) so the cloud playback fetch +
 *   clip-URL resolution can find the device. Idempotent: a slot cam (already has `bCamera`) maps to itself.
 *
 * buildDrawerSections — split the camera universe into the mock's sections:
 *   - loaded:       cams currently in slots, each with its slot index (SLOT badge + ✕ unassign).
 *   - available:    enabled cams on the CURRENT NVR not already loaded ("Other cameras on this NVR").
 *   - otherSystems: eview only — enabled cams on OTHER NVRs, grouped by serial (numeric-name sorted),
 *                   already-loaded cams removed (empty groups dropped).
 *   The client-side search (`filterText`) narrows loaded + available only (inventory §10 "filters
 *   loaded list only"); the component hides the Saved-layouts + Other-systems sections while a search
 *   is active (matches the mock), so otherSystems is computed unfiltered.
 */

const camKey = (serial, id) => `${serial}_${id}`;

export const normalizeSlotCamera = (cam, { currentSerial } = {}) => {
  if (!cam) return null;
  const slotCam = {
    bCamera: cam.bCamera != null ? cam.bCamera : cam.bID,
    bSerial: cam.bSerial != null ? cam.bSerial : currentSerial,
    sName: cam.sName,
  };
  if (cam.sServerName != null) slotCam.sServerName = cam.sServerName;
  if (cam.fEnable != null) slotCam.fEnable = cam.fEnable; // empty-slot marker (fEnable:false) — bare label, no prefix
  if (cam.serverUrl != null) slotCam.serverUrl = cam.serverUrl;
  if (cam.sDeviceID != null) slotCam.sDeviceID = cam.sDeviceID; // cloud: device the cam belongs to
  if (cam.source != null) slotCam.source = cam.source;
  return slotCam;
};

export const buildDrawerSections = ({
  slots = [],
  cameras = [],
  allCameras = [],
  authServers = [],
  isEviewSession = false,
  currentSerial,
  filterText = '',
} = {}) => {
  const ft = (filterText || '').trim().toLowerCase();

  // Loaded = filled slots, carrying their slot index (for the SLOT badge + ✕).
  const loadedAll = slots
    .map((cam, index) => (cam ? { cam, index } : null))
    .filter(Boolean);
  const loadedKeys = new Set(loadedAll.map(({ cam }) => camKey(cam.bSerial, cam.bCamera)));

  // Camera universe to pick from: eview = every NVR's cams; dview = the current NVR only.
  const universe = (isEviewSession ? allCameras : cameras).filter((c) => c.fEnable);

  const serverName = (serial) => {
    const fromCam = universe.find((c) => String(c.bSerial) === String(serial) && c.sServerName);
    if (fromCam) return fromCam.sServerName;
    const s = authServers.find((sv) => String(sv.bSerial) === String(serial));
    return (s && s.sName) || `System ${serial}`;
  };

  const matchesRaw = (c) => {
    if (!ft) return true;
    const idStr = String(c.bID != null ? c.bID : c.bCamera);
    return (c.sName || '').toLowerCase().includes(ft)
      || idStr.includes(ft)
      || (c.sServerName || '').toLowerCase().includes(ft);
  };

  const serialOf = (c) => (c.bSerial != null ? c.bSerial : currentSerial);

  // "Other cameras on this NVR" — enabled, on the current serial, not already loaded, matching search.
  const available = universe.filter((c) =>
    String(serialOf(c)) === String(currentSerial)
    && !loadedKeys.has(camKey(serialOf(c), c.bID))
    && matchesRaw(c));

  // Loaded rows respect the search too (so a filter narrows everything visible).
  const loaded = loadedAll.filter(({ cam }) => matchesRaw(cam));

  // "Other systems" (eview only) — cams on OTHER serials, grouped; loaded cams removed; unfiltered
  // (the component hides this whole section while searching).
  let otherSystems = [];
  if (isEviewSession) {
    const groups = {};
    universe.forEach((c) => {
      if (String(c.bSerial) === String(currentSerial)) return;
      if (loadedKeys.has(camKey(c.bSerial, c.bID))) return;
      const key = String(c.bSerial);
      if (!groups[key]) groups[key] = { serial: c.bSerial, name: serverName(c.bSerial), cameras: [] };
      groups[key].cameras.push(c);
    });
    otherSystems = Object.values(groups)
      .filter((g) => g.cameras.length > 0)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }));
  }

  return { loaded, available, otherSystems };
};
